import type { ReactNode } from 'react';
import type React from 'react';
import { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { Subscription } from 'rxjs';
import { createSSEObservable } from '../../../lib/createSSEObservable';
import { telemetry } from '../../../telemetry';
import type { AiChatQuestion, BuildDefinitionStatus } from '@server/ai';

interface SubscriptionInfo {
  subscription: Subscription;
  chatId: string;
  createdAt: Date;
}

type RawQuestion = {
  text: string;
  multiple_choice: boolean;
  options: string[];
};

type SSEMessageData =
  | {
      event: 'message' | 'artifact' | 'build_definition' | 'tool_explanation' | 'error';
      data: string;
    }
  | { event: 'artifact_metadata'; data: string | { artifact_id: string; title: string } }
  | { event: 'table_metadata'; data: string | { placeholder_id: string; table_id: string } }
  | { event: 'run_metadata'; data: { run_id: string; run_url: string } }
  | { event: 'keep-alive'; data: { timestamp: string } }
  | {
      event: 'research_handoff';
      data: { job_id: string; thread_id: string; additional_orgs: string[] };
    }
  | {
      event: 'build_definition_metadata' | 'build_definition_status';
      data: { build_definition_id: string; status: BuildDefinitionStatus };
    }
  | {
      event: 'questions';
      data: string | { questions: RawQuestion[] };
    }
  | { event: 'questions-metadata'; data: unknown };

interface ChatSubscriptionContextType {
  // State management only
  subscriptions: React.MutableRefObject<Map<string, SubscriptionInfo>>;

  // Simple subscription management
  addSubscription: (chatId: string, subscription: Subscription) => void;
  removeSubscription: (chatId: string) => void;
  hasSubscription: (chatId: string) => boolean;
}

const ChatSubscriptionContext = createContext<ChatSubscriptionContextType | undefined>(undefined);

interface ChatSubscriptionProviderProps {
  children: ReactNode;
}

export const ChatSubscriptionProvider: React.FC<ChatSubscriptionProviderProps> = ({ children }) => {
  const subscriptionsRef = useRef<Map<string, SubscriptionInfo>>(new Map());

  const addSubscription = useCallback((chatId: string, subscription: Subscription) => {
    // Remove existing subscription if any
    const existing = subscriptionsRef.current.get(chatId);
    if (existing) {
      existing.subscription.unsubscribe();
    }

    // Add new subscription
    subscriptionsRef.current.set(chatId, {
      subscription,
      chatId,
      createdAt: new Date(),
    });
  }, []);

  const removeSubscription = useCallback((chatId: string) => {
    const subscriptionInfo = subscriptionsRef.current.get(chatId);
    if (subscriptionInfo) {
      subscriptionInfo.subscription.unsubscribe();
      subscriptionsRef.current.delete(chatId);
    }
  }, []);

  const hasSubscription = useCallback((chatId: string) => {
    return subscriptionsRef.current.has(chatId);
  }, []);

  // Cleanup all subscriptions when the provider unmounts (app shutdown)
  useEffect(() => {
    const subscriptionsMap = subscriptionsRef.current;

    return () => {
      subscriptionsMap.forEach((info) => {
        info.subscription.unsubscribe();
      });
      subscriptionsMap.clear();
    };
  }, []);

  const contextValue: ChatSubscriptionContextType = {
    subscriptions: subscriptionsRef,
    addSubscription,
    removeSubscription,
    hasSubscription,
  };

  return (
    <ChatSubscriptionContext.Provider value={contextValue}>
      {children}
    </ChatSubscriptionContext.Provider>
  );
};

interface SubscriptionCallbacks {
  onMessage?: (chatId: string, chunk: any, isFirstContentChunk: boolean) => void;
  onComplete?: (chatId: string) => void;
  onError?: (chatId: string) => Promise<void>;
  onResearchHandoff?: (chatId: string) => void;
}

interface UseChatSubscriptionsReturn {
  subscribeToChat: (chatId: string, callbacks?: SubscriptionCallbacks) => Promise<boolean>;
  unsubscribeFromChat: (chatId: string) => void;
  isSubscribed: (chatId: string) => boolean;
}

export const useChatSubscriptions = (): UseChatSubscriptionsReturn => {
  const context = useContext(ChatSubscriptionContext);
  const { getAccessTokenSilently } = useAuth0();

  if (!context) {
    throw new Error('useChatSubscriptions must be used within ChatSubscriptionProvider');
  }

  const { addSubscription, removeSubscription, hasSubscription } = context;

  const subscribeToChat = useCallback(
    async (chatId: string, callbacks?: SubscriptionCallbacks): Promise<boolean> => {
      if (!chatId) {
        return false;
      }

      const url = import.meta.env.VITE_SERVER_URL + `/ai-agents/stream/${chatId}`;

      try {
        const token = await getAccessTokenSilently();
        if (!token) {
          telemetry.captureError(new Error(`Can't subscribe due to missing token`));
          return false;
        }

        let isFirstContentChunk = true;

        const isSubscribedPromise = new Promise<boolean>((resolve) => {
          const subscription = createSSEObservable(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }).subscribe({
            next: (data) => {
              resolve(true);

              try {
                if (!data) {
                  return;
                }

                const messageData: SSEMessageData = JSON.parse(data);

                // Determine if this is content-bearing (anything NOT metadata)
                const isRenderableContent =
                  messageData.event !== 'run_metadata' &&
                  messageData.event !== 'keep-alive' &&
                  messageData.event !== 'research_handoff' &&
                  messageData.event !== 'build_definition_status';
                const currentIsFirstContentChunk = isRenderableContent && isFirstContentChunk;

                if (isRenderableContent) {
                  isFirstContentChunk = false; // Mark that we've seen content
                }

                switch (messageData.event) {
                  case 'keep-alive':
                    return;
                  case 'build_definition_metadata':
                    // Parse the data which contains build_definition_id and status
                    const buildMetadata =
                      typeof messageData.data === 'string'
                        ? JSON.parse(messageData.data)
                        : messageData.data;
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        buildDefinitionMetadata: {
                          buildDefinitionId: buildMetadata.build_definition_id,
                          status: buildMetadata.status,
                        },
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;
                  case 'build_definition':
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        buildDefinitionContent: messageData.data,
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;
                  case 'build_definition_status':
                    const buildStatusData =
                      typeof messageData.data === 'string'
                        ? JSON.parse(messageData.data)
                        : messageData.data;
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        buildDefinitionStatus: {
                          buildDefinitionId: buildStatusData.build_definition_id,
                          status: buildStatusData.status,
                        },
                        isError: false,
                      },
                      false, // Never first content chunk for status updates
                    );
                    break;
                  case 'research_handoff':
                    callbacks?.onResearchHandoff?.(chatId);
                    return;

                  case 'run_metadata':
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        runId: messageData.data.run_id,
                        isError: false,
                      },
                      false, // Never first content chunk for metadata
                    );
                    break;

                  case 'tool_explanation':
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        reasoning: messageData.data,
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;
                  case 'error':
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        content: messageData.data,
                        isError: true,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;

                  case 'artifact_metadata':
                    // Parse the data which contains artifact_id, version_id, title, and tool_results
                    const metadata =
                      typeof messageData.data === 'string'
                        ? JSON.parse(messageData.data)
                        : messageData.data;
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        artifactMetadata: {
                          artifactId: metadata.artifact_id,
                          versionId: metadata.version_id,
                          title: metadata.title.replace(/^"|"$/g, ''), // Remove quotes from title
                          toolResults: metadata.tool_results, // Pass tool_results data to frontend
                        },
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;

                  case 'artifact':
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        artifactContent: messageData.data,
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;

                  case 'table_metadata':
                    // Parse the data which contains placeholder_id and table_id
                    const tableMetadata =
                      typeof messageData.data === 'string'
                        ? JSON.parse(messageData.data)
                        : messageData.data;
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        tableMetadata: {
                          placeholderId: tableMetadata.placeholder_id,
                          tableId: tableMetadata.table_id,
                        },
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;

                  case 'questions': {
                    const questionsPayload =
                      typeof messageData.data === 'string'
                        ? JSON.parse(messageData.data)
                        : messageData.data;
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        questionsData: questionsPayload.questions.map(
                          (q: RawQuestion): AiChatQuestion => ({
                            text: q.text,
                            multipleChoice: q.multiple_choice,
                            options: q.options,
                          }),
                        ),
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                    break;
                  }

                  case 'questions-metadata':
                    callbacks?.onMessage?.(
                      chatId,
                      { questionsMetadata: true, isError: false },
                      currentIsFirstContentChunk,
                    );
                    break;

                  case 'message':
                  default:
                    callbacks?.onMessage?.(
                      chatId,
                      {
                        content: messageData.data,
                        isError: false,
                      },
                      currentIsFirstContentChunk,
                    );
                }

                if (currentIsFirstContentChunk) {
                  isFirstContentChunk = false;
                }
              } catch (err) {
                telemetry.captureError(new Error('Error parsing SSE message'));
              }
            },
            error: () => {
              telemetry.addBreadcrumb({
                category: 'sse',
                message: 'Stream not found or expired',
                level: 'warning',
              });
              resolve(false);

              if (callbacks?.onError) {
                callbacks.onError(chatId);
              }

              removeSubscription(chatId);
            },
            complete: () => {
              resolve(true);

              if (callbacks?.onComplete) {
                callbacks.onComplete(chatId);
              }

              removeSubscription(chatId);
            },
          });

          // Store the subscription using context
          addSubscription(chatId, subscription);
        });

        return await isSubscribedPromise;
      } catch (error) {
        telemetry.captureError(error);
        return false;
      }
    },
    [getAccessTokenSilently, addSubscription, removeSubscription],
  );

  const unsubscribeFromChat = useCallback(
    (chatId: string) => {
      removeSubscription(chatId);
    },
    [removeSubscription],
  );

  const isSubscribed = useCallback(
    (chatId: string) => {
      return hasSubscription(chatId);
    },
    [hasSubscription],
  );

  return {
    subscribeToChat,
    unsubscribeFromChat,
    isSubscribed,
  };
};
